// components/list/ListPermissionPrompt.tsx
// Claude Code's permission prompt, adapted to List: a question, the details of what would change,
// and Deny / Always allow / Allow once with their keys. It replaces the composer while it waits.
import React, { useCallback, useEffect, useRef } from 'react';
import { useKernelStore } from '@/kernel/kernelStores';
import { answerListPermission, type ListPermissionDecision } from '#lib/list/listPermission';
import type { ListPermissionRequest } from '#types/types';

// globals.css pins div/span/button to 12px: every type size here is inline.
const Key: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark }) => (
  <span className={dark ? 'rounded-[3px] border border-black/20 px-1 text-black/70' : 'rounded-[3px] border border-white/15 px-1 text-white/55'}
    style={{ fontSize: 10, lineHeight: '14px' }}>{children}</span>
);

export const selectListPermission = (sessionId: string | null) => (state: { entityKernelState: Record<string, { state?: unknown } | undefined> }) =>
  sessionId ? ((state.entityKernelState[`ChatSession:${sessionId}`]?.state as { runtime?: { listPermission?: ListPermissionRequest | null } } | undefined)?.runtime?.listPermission ?? null) : null;

/** onAnswer: another asker (the Daimon) takes the answer itself, and offers no 'Always allow'. */
export const ListPermissionPrompt: React.FC<{
  request: Pick<ListPermissionRequest, 'id' | 'title' | 'label' | 'items'>;
  onAnswer?: (decision: ListPermissionDecision) => void;
  disabled?: boolean;
  error?: string;
}> = ({ request, onAnswer, disabled = false, error }) => {
  const promptRef = useRef<HTMLDivElement>(null);
  const lang = useKernelStore((state) => (state.language === 'fr' ? 'fr' : 'en'));
  const answer = useCallback((decision: ListPermissionDecision) => {
    if (disabled) return;
    if (!onAnswer) answerListPermission(request.id, decision);
    else if (decision !== 'always') onAnswer(decision);
  }, [request.id, onAnswer, disabled]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (disabled || event.defaultPrevented) return;
      if (document.querySelectorAll('[data-permission-prompt]').length > 1
        && (!(event.target instanceof Node) || !promptRef.current?.contains(event.target))) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Beside a live composer, Enter belongs to what is being typed, never to an approval.
      if (onAnswer && (event.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable="true"]')) return;
      const decision: ListPermissionDecision | null = event.key === '1' || event.key === 'Escape' ? 'deny'
        : event.key === '2' ? 'always' : event.key === '3' || event.key === 'Enter' ? 'once' : null;
      if (!decision || (onAnswer && decision === 'always')) return;
      event.preventDefault();
      answer(decision);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [answer, onAnswer, disabled]);
  const copy = lang === 'fr'
    ? { deny: 'Refuser', always: 'Toujours autoriser', once: 'Autoriser une fois' }
    : { deny: 'Deny', always: 'Always allow', once: 'Allow once' };
  return (
    <div ref={promptRef} data-permission-prompt={request.id} role="dialog" aria-busy={disabled} aria-label={request.title} className="rounded-2xl border border-white/10 bg-[#1f1f1f] p-4">
      <div className="font-medium text-white" style={{ fontSize: 14 }}>{request.title}</div>
      <div className="mt-3 grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-6 rounded-xl bg-white/[0.04] p-3">
        <div className="text-white/45" style={{ fontSize: 13 }}>{request.label}</div>
        <div className="flex flex-wrap items-center gap-2">
          {request.items.map((item) => (
            <div key={item.id} className="flex items-center gap-2">
              {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt="" className="h-9 w-9 rounded-md object-cover" /> : null}
              <span className="text-white/85" style={{ fontSize: 13 }}>{item.name}</span>
            </div>
          ))}
        </div>
      </div>
      {error && <p role="alert" className="mt-3 text-[#F2A6A6]">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button disabled={disabled} type="button" onClick={() => answer('deny')} className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-white/[0.06] px-2 py-1 text-white/85 hover:bg-white/10" style={{ fontSize: 12 }}>
          {copy.deny} <Key>1</Key> <Key>Esc</Key>
        </button>
        <div className="flex items-center gap-2">
          {onAnswer ? null : (
            <button disabled={disabled} type="button" onClick={() => answer('always')} className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-white/[0.06] px-2 py-1 text-white/85 hover:bg-white/10" style={{ fontSize: 12 }}>
              {copy.always} <Key>2</Key>
            </button>
          )}
          <button disabled={disabled} type="button" autoFocus={!onAnswer} onClick={() => answer('once')} className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-white px-2 py-1 font-medium text-black hover:bg-white/90" style={{ fontSize: 12 }}>
            {copy.once} <Key dark>3</Key> <Key dark>↵</Key>
          </button>
        </div>
      </div>
    </div>
  );
};
